import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  View,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  StyleSheet,
  Pressable,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import LinearGradient from "react-native-linear-gradient";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import Text from "@components/CustomText";
import color from "@constants/color";
import { getReconInfo } from "@services/api";
import { ScaledSheet } from "@utils/scale";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AmountEntry {
  title: string;
  amount: string;
}

interface ReconInfo {
  total_collected?: AmountEntry;
  collected_amounts?: {
    COD?: AmountEntry;
    PREPAID?: AmountEntry;
  };
  status?: string;
  status_txt?: string;
}

interface OnlinePayment {
  amount_to_collect?: number | string;
  cta_text?: string;
  recon_id?: number | string | null;
  [key: string]: unknown;
}

interface OrderSummaryItem {
  key?: string;
  title: string;
  value: string | number;
  show_rating?: boolean;
  rating?: string;
  rating_color?: string;
}

interface OrderSummary {
  title: string;
  list?: OrderSummaryItem[];
}

// "Collect Cash before recon": the spoke records cash this DB handed over
// during the day; the DB reads the OTP off this screen. Display only.
interface CollectedCashPending {
  trip_id: string;
  amount: number;
  amount_text: string;
  amount_title: string;
  otp: string | number;
  subtitle: string;
  expires_in_title: string;
  expired_text: string;
  expires_at: string;
  server_time: string;
}

interface CollectedCashTxn {
  amount: number;
  amount_text: string;
  verified_at: string;
  verified_text: string;
}

interface CollectedCash {
  title: string;
  total_collected: number;
  total_collected_text: string;
  total_collected_title: string;
  remaining_text: string;
  remaining_title: string;
  pending_request: CollectedCashPending | null;
  txns: CollectedCashTxn[];
}

interface ReconData {
  recon_otp?: string | number;
  recon_info?: ReconInfo;
  recon_status?: string;
  online_payment?: OnlinePayment;
  order_summary?: OrderSummary;
  collected_cash?: CollectedCash;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ReconOtpCardProps {
  otp: string | number;
}

const ReconOtpCard: React.FC<ReconOtpCardProps> = ({ otp }) => {
  const digits = String(otp).split("");

  return (
    <LinearGradient
      colors={["#7C3AED", "#5B21B6"]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.otpCard}
    >
      <Text style={styles.otpLabel}>Recon OTP</Text>
      <View style={styles.otpDigitsRow}>
        {digits.map((digit, index) => (
          <View key={index} style={styles.otpDigitTile}>
            <Text style={styles.otpDigit}>{digit}</Text>
          </View>
        ))}
      </View>
    </LinearGradient>
  );
};

// ---------------------------------------------------------------------------

const formatClock = (ts?: string): string => {
  if (!ts) return "";
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

interface CashHandoverCardProps {
  data: CollectedCash;
}

// Read-only card. Shows the amount the spoke is recording, the 4-digit code
// the DB reads out, a countdown (drift-free: expires_at - server_time), and
// the list of handovers already verified today. No actions.
const CashHandoverCard: React.FC<CashHandoverCardProps> = ({ data }) => {
  const pending = data.pending_request;
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!pending) return undefined;
    const offset = pending.server_time
      ? new Date(pending.server_time).getTime() - Date.now()
      : 0;
    const expiresMs = new Date(pending.expires_at).getTime();
    const tick = () => {
      const left = Math.max(
        0,
        Math.floor((expiresMs - (Date.now() + offset)) / 1000),
      );
      setSecondsLeft(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [pending]);

  const countdown = `${Math.floor(secondsLeft / 60)}:${String(
    secondsLeft % 60,
  ).padStart(2, "0")}`;
  const digits = pending ? String(pending.otp).split("") : [];

  return (
    <View style={styles.cashCard}>
      <Text style={styles.cashLabel}>{data.title}</Text>

      {pending && (
        <View style={styles.cashPending}>
          <Text style={styles.cashSub}>{pending.amount_title}</Text>
          <Text style={styles.cashAmount}>{pending.amount_text}</Text>
          {secondsLeft > 0 ? (
            <>
              <Text style={styles.cashSub}>{pending.subtitle}</Text>
              <View style={styles.otpDigitsRow}>
                {digits.map((digit, index) => (
                  <View key={index} style={styles.cashDigitTile}>
                    <Text style={styles.otpDigit}>{digit}</Text>
                  </View>
                ))}
              </View>
              <Text style={styles.cashSub}>
                {pending.expires_in_title} {countdown}
              </Text>
            </>
          ) : (
            <Text style={styles.cashExpired}>{pending.expired_text}</Text>
          )}
        </View>
      )}

      {data.txns.length > 0 && (
        <View style={pending ? styles.cashTxnsWithPending : undefined}>
          {data.txns.map((t, i) => (
            <View key={i} style={styles.cashTxnRow}>
              <Text style={styles.cashTxnText}>
                {formatClock(t.verified_at)} · {t.amount_text}
              </Text>
              <Text style={styles.cashTxnVerified}>✓ {t.verified_text}</Text>
            </View>
          ))}
          <View style={styles.cashTxnRow}>
            <Text style={styles.cashTotalText}>
              {data.total_collected_title}
            </Text>
            <Text style={styles.cashTotalText}>
              {data.total_collected_text}
            </Text>
          </View>
          <Text style={styles.cashRemaining}>
            {data.remaining_title}: {data.remaining_text}
          </Text>
        </View>
      )}
    </View>
  );
};

// ---------------------------------------------------------------------------

interface AmountSummaryCardProps {
  reconInfo: ReconInfo;
  onlinePayment?: OnlinePayment;
  onPayNowPress: () => void;
}

const AmountSummaryCard: React.FC<AmountSummaryCardProps> = ({
  reconInfo,
  onlinePayment,
  onPayNowPress,
}) => (
  <View style={styles.card}>
    {reconInfo.total_collected && (
      <View style={styles.row}>
        <Text style={styles.mainTitle}>{reconInfo.total_collected.title}</Text>
        <Text variant="heading_24" style={styles.totalAmount}>
          {reconInfo.total_collected.amount}
        </Text>
      </View>
    )}

    <View style={styles.thinDivider} />

    {reconInfo.collected_amounts?.COD && (
      <View style={[styles.row, styles.subRow]}>
        <Text style={styles.label}>
          {reconInfo.collected_amounts.COD.title}
        </Text>
        <Text variant="title_20">{reconInfo.collected_amounts.COD.amount}</Text>
      </View>
    )}

    {reconInfo.collected_amounts?.PREPAID && (
      <View style={[styles.row, styles.subRow]}>
        <Text style={styles.label}>
          {reconInfo.collected_amounts.PREPAID.title}
        </Text>
        <Text variant="title_20">
          {reconInfo.collected_amounts.PREPAID.amount}
        </Text>
      </View>
    )}

    <View style={styles.subRowDivider} />

    <View style={styles.buttonRow}>
      {onlinePayment?.amount_to_collect ? (
        <Pressable
          style={({ pressed }) => [
            styles.payButton,
            pressed && styles.buttonPressed,
          ]}
          onPress={onPayNowPress}
          testID="reconPayNowButton"
          accessibilityRole="button"
          accessibilityLabel="Pay now"
        >
          <Text style={styles.payButtonText}>
            {onlinePayment.cta_text || "Pay Now"}
          </Text>
        </Pressable>
      ) : (
        <View style={styles.payButtonDisabled}>
          <Text style={styles.payButtonTextDisabled}>{"Pay Now"}</Text>
        </View>
      )}
    </View>
  </View>
);

// ---------------------------------------------------------------------------

interface ReconStatusCardProps {
  statusText: string;
  statusColor: string;
}

const ReconStatusCard: React.FC<ReconStatusCardProps> = ({
  statusText,
  statusColor,
}) => (
  <View style={styles.card}>
    <View style={styles.row}>
      <Text style={styles.mainTitle}>Recon Status</Text>
      <Text style={[styles.statusValue, { color: statusColor }]}>
        {statusText}
      </Text>
    </View>
  </View>
);

// ---------------------------------------------------------------------------

interface OrderSummaryCardProps {
  orderSummary: OrderSummary;
  getRatingColor: (ratingColor: string) => string;
}

const OrderSummaryCard: React.FC<OrderSummaryCardProps> = ({
  orderSummary,
  getRatingColor,
}) => (
  <View style={styles.card}>
    <Text style={styles.sectionTitle}>{orderSummary.title}</Text>

    {orderSummary.list?.map((item, index) => {
      const isRatingRow = index === (orderSummary.list?.length ?? 0) - 1;

      return (
        <React.Fragment key={item.key ?? index}>
          {isRatingRow && <View style={styles.thinDivider} />}
          <View style={[styles.row, styles.summaryRow]}>
            <Text style={styles.label}>{item.title}</Text>
            {item.show_rating && item.rating ? (
              <View style={styles.valueWithBadge}>
                <View
                  style={[
                    styles.ratingBadge,
                    {
                      backgroundColor:
                        getRatingColor(item.rating_color ?? "") + "1A",
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.ratingText,
                      {
                        color: getRatingColor(item.rating_color ?? ""),
                      },
                    ]}
                  >
                    {item.rating}
                  </Text>
                </View>
                <Text variant="title_20" style={styles.summaryValue}>
                  {item.value}
                </Text>
              </View>
            ) : (
              <Text variant="title_20" style={styles.summaryValue}>
                {item.value}
              </Text>
            )}
          </View>
        </React.Fragment>
      );
    })}
  </View>
);

// ---------------------------------------------------------------------------
// Main screen
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ReconDetails = ({ navigation, route }: any) => {
  const [reconData, setReconData] = useState<ReconData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasFetchedOnceRef = useRef(false);
  const { nav_params } = route.params;
  const { recon_date } = nav_params;

  // Refetch on every focus so returning from CpOnlinePaymentV2 reflects
  // the new amount_to_collect immediately. First focus shows the loader;
  // subsequent focuses refetch silently to avoid a flash on re-entry.
  useFocusEffect(
    useCallback(() => {
      fetchReconData(recon_date ?? "", hasFetchedOnceRef.current);
      hasFetchedOnceRef.current = true;
    }, [recon_date]),
  );

  // "Collect Cash before recon": the spoke's OTP request must show up without
  // the DB pulling to refresh, so poll while this screen is focused and the
  // recon is still open. Stops on blur and once the recon is completed.
  const isReconCompleted =
    String(reconData?.recon_info?.status ?? "").toUpperCase() === "COMPLETED";
  useFocusEffect(
    useCallback(() => {
      if (isReconCompleted) return undefined;
      const id = setInterval(() => {
        fetchReconData(recon_date ?? "", true);
      }, 15000);
      return () => clearInterval(id);
    }, [recon_date, isReconCompleted]),
  );

  const fetchReconData = async (reconDate: string, silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const response = await getReconInfo(reconDate);
      setReconData(response?.data?.data ?? {});
    } catch (error) {
      console.error("Error fetching recon data:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    fetchReconData(recon_date ?? "", true);
  }, [recon_date]);

  const getStatusColor = useCallback((status: string): string => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "#4A90E2";
      case "completed":
        return color.lima;
      case "overdue":
        return color.errorRed;
      default:
        return "#4A90E2";
    }
  }, []);

  const getRatingColor = useCallback((ratingColor: string): string => {
    switch (ratingColor?.toLowerCase()) {
      case "red":
        return "#E53935";
      case "green":
        return "#4CAF50";
      default:
        return "#4CAF50";
    }
  }, []);

  const handlePayNowPress = useCallback(() => {
    navigation.navigate("CpOnlinePaymentV2", {
      ledger: reconData?.online_payment,
    });
  }, [navigation, reconData?.online_payment]);

  const headerProps = {
    headertitle: "Recon",
    navigation,
    route,
    hideShadow: false,
    subHeaderTitle: "",
    headerStyle: {},
    rightIcon: undefined,
    RightButton: undefined,
    goingBackHandler: undefined,
    IsRightIconDropDown: undefined,
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <HeaderWithBackAndTitle {...headerProps} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      </View>
    );
  }

  if (!reconData) {
    return (
      <View style={styles.container}>
        <HeaderWithBackAndTitle {...headerProps} />
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>No data available</Text>
        </View>
      </View>
    );
  }

  const statusColor = getStatusColor(reconData.recon_status ?? "");

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle {...headerProps} />

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            colors={[color.primary]}
            tintColor={color.primary}
          />
        }
      >
        {reconData.recon_otp && <ReconOtpCard otp={reconData.recon_otp} />}

        {reconData.collected_cash && (
          <CashHandoverCard data={reconData.collected_cash} />
        )}

        {reconData.recon_info && (
          <AmountSummaryCard
            reconInfo={reconData.recon_info}
            onlinePayment={reconData.online_payment}
            onPayNowPress={handlePayNowPress}
          />
        )}

        <ReconStatusCard
          statusText={reconData.recon_info?.status_txt ?? "N/A"}
          statusColor={statusColor}
        />

        {reconData.order_summary && (
          <OrderSummaryCard
            orderSummary={reconData.order_summary}
            getRatingColor={getRatingColor}
          />
        )}
      </ScrollView>
    </View>
  );
};

export default ReconDetails;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = ScaledSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F4F5F8",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 8,
    paddingBottom: 24,
  },

  // --- OTP Card ---
  otpCard: {
    borderRadius: 16,
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 24,
    // iOS shadow
    shadowColor: "#7C3AED",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    // Android elevation
    elevation: 6,
  },
  otpLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "rgba(255, 255, 255, 0.85)",
    marginBottom: 16,
  },
  otpDigitsRow: {
    flexDirection: "row",
    gap: 12,
  },
  otpDigitTile: {
    flex: 1,
    aspectRatio: 1,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    // subtle border for frosted look
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  otpDigit: {
    fontSize: 32,
    fontWeight: "700",
    color: color.white,
  },

  // --- Cash handover card (Collect Cash before recon) ---
  cashCard: {
    borderRadius: 16,
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 18,
    backgroundColor: "#0B6E5B",
    shadowColor: "#0B6E5B",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
  },
  cashLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "rgba(255, 255, 255, 0.85)",
    marginBottom: 10,
  },
  cashPending: {
    marginBottom: 4,
  },
  cashSub: {
    fontSize: 13,
    color: "rgba(255, 255, 255, 0.85)",
    marginTop: 8,
  },
  cashAmount: {
    fontSize: 26,
    fontWeight: "700",
    color: color.white,
    marginTop: 2,
  },
  cashDigitTile: {
    flex: 1,
    aspectRatio: 1,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.3)",
    marginTop: 10,
  },
  cashExpired: {
    fontSize: 14,
    fontWeight: "600",
    color: "#FFD7D7",
    marginTop: 10,
  },
  cashTxnsWithPending: {
    borderTopWidth: 1,
    borderTopColor: "rgba(255, 255, 255, 0.25)",
    marginTop: 12,
    paddingTop: 8,
  },
  cashTxnRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 4,
  },
  cashTxnText: {
    fontSize: 13,
    color: "rgba(255, 255, 255, 0.9)",
  },
  cashTxnVerified: {
    fontSize: 12,
    fontWeight: "700",
    color: color.white,
  },
  cashTotalText: {
    fontSize: 14,
    fontWeight: "700",
    color: color.white,
  },
  cashRemaining: {
    fontSize: 12,
    color: "rgba(255, 255, 255, 0.75)",
    marginTop: 4,
  },

  // --- Shared card ---
  card: {
    backgroundColor: color.white,
    borderRadius: 16,
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 20,
    paddingVertical: 18,
    // iOS shadow
    shadowColor: color.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    // Android elevation
    elevation: 2,
  },

  // --- Amount summary ---
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  subRow: {
    marginTop: 8,
  },
  mainTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: color.black,
    flex: 1,
  },
  totalAmount: {
    fontWeight: "700",
    color: color.black,
  },
  thinDivider: {
    height: 1,
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    marginTop: 12,
    marginBottom: 8,
  },
  subRowDivider: {
    height: 1,
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    marginTop: 12,
    marginBottom: 0,
  },
  label: {
    fontSize: 14,
    fontWeight: "400",
    color: color.mediumBlacklightBlack,
    flex: 1,
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 12,
  },
  payButton: {
    width: "33%",
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: color.plain_green,
    alignItems: "center",
    justifyContent: "center",
  },
  payButtonText: {
    fontSize: 15,
    fontWeight: "700",
    color: color.white,
  },
  payButtonDisabled: {
    width: "33%",
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: color.disableGrey,
    alignItems: "center",
    justifyContent: "center",
  },
  payButtonTextDisabled: {
    fontSize: 15,
    fontWeight: "700",
    color: color.disableFont,
  },
  buttonPressed: {
    opacity: 0.75,
  },

  // --- Status card ---
  statusValue: {
    fontSize: 16,
    fontWeight: "700",
  },

  // --- Order summary ---
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: color.black,
    marginBottom: 12,
  },
  summaryRow: {
    paddingVertical: 4,
  },
  summaryValue: {
    fontWeight: "700",
  },
  valueWithBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  ratingBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
  },
  ratingText: {
    fontSize: 12,
    fontWeight: "700",
  },

  // --- Loading / error ---
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  errorText: {
    fontSize: 16,
    color: color.mediumBlacklightBlack,
  },
}) as ReturnType<typeof StyleSheet.create>;
